import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mic, Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doLogin = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    login(data.token, data.user);
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await doLogin(form.email, form.password);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain">
      {/* form side */}
      <div className="flex items-center justify-center px-6 py-12 bg-[#F9F9F6]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-[#4A6B53] flex items-center justify-center shadow-[0_8px_16px_rgba(74,107,83,0.3)]">
              <Mic className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight text-[#121814]">
              PSR Notes AI
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#121814] leading-[1.05]">
            Welcome back.
          </h1>
          <p className="mt-4 text-[#7A8C82] text-base leading-relaxed">
            Sign in to turn your words into billable PSR notes.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#7A8C82] uppercase tracking-wider">
                Email
              </Label>
              <Input
                data-testid="email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@agency.org"
                className="h-12 bg-white border-[#E2DFD8]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#7A8C82] uppercase tracking-wider">
                Password
              </Label>
              <Input
                data-testid="password-input"
                type="password"
                required
                value={form.password}
                onChange={set("password")}
                placeholder="••••••••"
                className="h-12 bg-white border-[#E2DFD8]"
              />
            </div>

            <Button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)] transition-colors"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-[#7A8C82]" data-testid="no-signup-note">
            Need a login or forgot your password? Ask your QP or administrator — they create and reset staff accounts.
          </p>
        </div>
      </div>

      {/* visual side */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#4A6B53] items-center justify-center p-16">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#5E8268] blur-3xl opacity-60" />
        <div className="absolute -bottom-32 -right-16 w-[28rem] h-[28rem] rounded-full bg-[#3D5A45] blur-3xl opacity-70" />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-[#D4A373] blur-3xl opacity-30" />
        <div className="relative z-10 max-w-md">
          <Sparkles className="w-10 h-10 text-[#DCE5DF] mb-8" strokeWidth={1.5} />
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-white leading-snug">
            Focus on the person, <br />
            <span className="font-extrabold">not the paperwork.</span>
          </h2>
          <p className="mt-6 text-[#DCE5DF] text-base leading-relaxed">
            Just talk. Describe your session in your own words and we'll write a
            clean, compliant, billable PSR note for you — built to NC DHHS &
            Alliance Health standards.
          </p>
        </div>
      </div>
    </div>
  );
}
